"use client";

import { getTrackingInfo } from "@/components/amc-pixel/amc-pixel-utils";
import { gtmEventsService } from "@/components/third-party-scripts/google-tag-manager/gtm-events-service";
import { useGtm } from "@/hooks/useGtm";
import useReplays from "@/hooks/useReplays";
import useTracking from "@/hooks/useTracking";
import {
  getOptimizelyClient,
  getPreviousOptimizelyTestInfo,
  OptimizelyDecisionNotification,
  OptimizelyUser,
  updateOptimizelyInfo,
} from "@/lib/optimizely";
import { OPTIMIZELY_ROUTE_FLAGS } from "@/lib/optimizely-constants";
import {
  getCtvExperimentAssignment,
  getForcedCtvVariationKey,
  isCtvQaUserId,
  resolveCtvAssignmentId,
} from "@/lib/ctv-dmas";
import { setCookie } from "@/utilities/cookie";
import { debugLog } from "@/utilities/debug";
import { getSessionStorageItem } from "@/utilities/storage";
import { DeviceIdUtil } from "@amerisave/amc-client-beacon";
import {
  enums,
  OptimizelyDecision,
  OptimizelyProvider,
} from "@optimizely/react-sdk";
import { usePathname } from "next/navigation";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

interface IAnalyticsContextProps {
  optimizelyDecisionsRan: boolean;
  optimizelyUser: OptimizelyUser;
  setOptimizelyUser: (user: OptimizelyUser) => void;
  getOptimizelyDecisionInfo: (flagKey: string) => string;
  setOptimizelyUserAttribute: (
    key: string,
    value: string | boolean | number,
  ) => void;
  oneTrustGroupsReady: boolean;
  setOneTrustGroupsReady: (ready: boolean) => void;
}

interface IAnalyticsContextProviderProps {
  children: ReactNode;
  optimizelyUser: OptimizelyUser;
}

const AnalyticsContext = createContext<IAnalyticsContextProps | null>(null);

const AnalyticsProvider = (props: IAnalyticsContextProviderProps) => {
  const optimizely = getOptimizelyClient();
  const { optimizelyUser: initialOptimizelyUser, children } = props;
  const pathname = usePathname();
  useTracking({ optimizelyUser: initialOptimizelyUser });
  const { trackCustom } = useGtm();
  const { sessionId: openReplaySessionId, sessionUrl: openReplaySessionUrl } =
    useReplays();
  const [optimizelyGTMDataSet, setOptimizelyGTMDataSet] = useState(false);
  const [gtmOpenReplayDataSet, setGtmOpenReplayDataSet] = useState(false);
  const [gaSessionInserted, setGASessionInserted] = useState(false);
  const [optimizelyDecisionEvent, setOptimizelyDecisionEvent] =
    useState<OptimizelyDecisionNotification | null>(null);
  const [optimizelyUser, setOptimizelyUser] = useState(initialOptimizelyUser);
  const [testStarted, setTestStarted] = useState(false);
  const [oneTrustGroupsReady, setOneTrustGroupsReady] = useState(false);
  const [optimizelyDecisionsRan, setOptimizelyDecisionsRan] = useState(
    !!initialOptimizelyUser.attributes.isInTest,
  );
  const [optimizelyReady, setOptimizelyReady] = useState(
    Object.keys(OPTIMIZELY_ROUTE_FLAGS).length > 0 ? false : true,
  );
  const decidesRunKey = useRef<string | null>(null);

  const insertGAInfo = () => {
    if (!gaSessionInserted) {
      gtmEventsService.insertSession();
      setGASessionInserted(true);
    }
  };

  const insertTrackingInfo = async () => {
    const trackingInfo = await getTrackingInfo();
    const deviceId = new DeviceIdUtil().get();
    const { visitorId, sourceInfoId } = trackingInfo;

    await updateOptimizelyInfo({
      sessionId: optimizelyUser.attributes.sessionId.toString(),
      deviceId,
      visitorId: Number(visitorId),
      sourceInfoId,
    });
  };

  const onStorageChange = () => {
    const optimizelyTestRunning = getSessionStorageItem(
      "optimizelyTestRunning",
    );

    if (optimizelyTestRunning === "true") {
      setTestStarted(true);
    }
  };

  const setGTMOptimizelyInfo = async (
    event: OptimizelyDecisionNotification,
  ) => {
    if (
      !optimizelyDecisionEvent &&
      event.decisionInfo &&
      event.decisionInfo.flagKey &&
      event.decisionInfo.ruleKey &&
      event.decisionInfo.variationKey &&
      event.decisionInfo.decisionEventDispatched
    ) {
      const forcedVariationKey = getForcedCtvVariationKey(
        event.decisionInfo.flagKey,
        event.attributes,
        event.decisionInfo.enabled,
      );

      setOptimizelyDecisionEvent(
        forcedVariationKey
          ? {
              ...event,
              decisionInfo: {
                ...event.decisionInfo,
                variationKey: forcedVariationKey,
              },
            }
          : event,
      );
    }
  };

  const setOptimizelyGTMInfo = async () => {
    if (!gtmEventsService?.isInitialized || optimizelyGTMDataSet) {
      return;
    }

    const gtmData = await gtmEventsService.getBaseEventData();

    setOptimizelyUser((prevUser) => ({
      ...prevUser,
      attributes: {
        ...prevUser.attributes,
        utm_campaign: gtmData.funnelCampaign || "",
        utm_medium: gtmData.funnelMedium || "",
        utm_source: gtmData.funnelSource || "",
      },
    }));

    setOptimizelyGTMDataSet(true);
  };

  /**
   * Returns the Optimizely variation key for a flag
   * ONLY when the current route is explicitly mapped to that flag.
   * This prevents Optimizely decisions from leaking onto unrelated pages
   * during SPA navigation.
   */
  const getOptimizelyDecisionInfo = (flagKey: string) => {
    const optimizelyRouteMatched = [
      ...(OPTIMIZELY_ROUTE_FLAGS[pathname] || []),
      ...(OPTIMIZELY_ROUTE_FLAGS["all"] || []),
    ];

    if (optimizelyRouteMatched?.includes(flagKey)) {
      return String(optimizelyUser.attributes[`${flagKey}VariationKey`] ?? "");
    }

    return "";
  };

  const runOptimizelyDecides = async (pathname: string) => {
    const optimizelyRouteMatched = [
      ...(OPTIMIZELY_ROUTE_FLAGS[pathname] || []),
      ...(OPTIMIZELY_ROUTE_FLAGS["all"] || []),
    ];

    if (!optimizelyRouteMatched.length) {
      setOptimizelyDecisionsRan(true);
      return;
    }

    const qaUserId = isCtvQaUserId(optimizelyUser.id)
      ? optimizelyUser.id
      : null;
    const needsDma = optimizelyRouteMatched.some(
      (flagKey) => flagKey === "AS202",
    );
    const ctvAssignment = needsDma
      ? getCtvExperimentAssignment(qaUserId)
      : null;

    debugLog("[AS202] DMA", optimizelyUser.attributes, ctvAssignment);

    for (const flagKey of optimizelyRouteMatched) {
      const isCtvFlag = flagKey === "AS202";

      if (ctvAssignment && isCtvFlag && flagKey !== ctvAssignment.flagKey) {
        continue;
      }

      const decision = optimizely.decide(flagKey);
      const hasForcedDmaVariation = Boolean(
        ctvAssignment &&
        flagKey === ctvAssignment.flagKey &&
        ctvAssignment.variationKey,
      );

      decision.flagKey = flagKey;

      if (hasForcedDmaVariation) {
        const allowForcedVariation = Boolean(qaUserId) || decision.enabled;

        decision.variationKey = allowForcedVariation
          ? ctvAssignment!.variationKey || ""
          : "control";
        decision.enabled = allowForcedVariation;
      } else if (flagKey === "AS202") {
        decision.variationKey = decision.enabled
          ? decision.variationKey || "control"
          : "control";
      }

      const resolvedAssignmentId = ctvAssignment
        ? (ctvAssignment.assignmentId ??
          resolveCtvAssignmentId(decision.variationKey))
        : null;

      if (resolvedAssignmentId) {
        setOptimizelyUser((prevUser) => ({
          ...prevUser,
          attributes: {
            ...prevUser.attributes,
            ctvAssignment: resolvedAssignmentId,
          },
        }));
      }

      debugLog("optimizely decision", decision.flagKey, decision);

      if (decision.enabled) {
        setOptimizelyTestInfo(decision, true);
      } else if (isCtvFlag) {
        clearOptimizelyTestFlag(decision.flagKey);
      }
    }

    setOptimizelyDecisionsRan(true);
  };

  const clearOptimizelyTestFlag = (flagKey: string) => {
    const tests = getPreviousOptimizelyTestInfo();

    if (!tests[flagKey]) {
      setOptimizelyUser((prevUser) => ({
        ...prevUser,
        attributes: {
          ...prevUser.attributes,
          [flagKey]: false,
          [`${flagKey}VariationKey`]: "control",
        },
      }));
      return;
    }

    delete tests[flagKey];

    setOptimizelyUser((prevUser) => ({
      ...prevUser,
      attributes: {
        ...prevUser.attributes,
        [flagKey]: false,
        [`${flagKey}VariationKey`]: "control",
        isInTest: Object.keys(tests).length > 0,
      },
    }));

    setCookie("optimizelyFeatureTests", tests, ".amerisave.com");
  };

  const setOptimizelyTestInfo = (
    decision: OptimizelyDecision,
    overwrite = false,
  ) => {
    const tests = getPreviousOptimizelyTestInfo();

    if (tests[decision.flagKey] && !overwrite) {
      return;
    }

    tests[decision.flagKey] = {
      event: "optimizely-decision-fs",
      "optimizely-flagKey": decision.flagKey,
      "optimizely-ruleKey": decision.ruleKey,
      "optimizely-variationKey": decision.variationKey,
      decisionEventDispatched: true,
    };

    setOptimizelyUser((prevUser) => ({
      ...prevUser,
      attributes: {
        ...prevUser.attributes,
        [decision.flagKey]: true,
        [`${decision.flagKey}VariationKey`]: decision.variationKey || "",
        isInTest: Object.keys(tests).length > 0,
      },
    }));

    setCookie("optimizelyFeatureTests", tests, ".amerisave.com");
  };

  const setOptimizelyUserAttribute = (
    key: string,
    value: string | boolean | number,
  ) => {
    setOptimizelyUser((prevUser) => ({
      ...prevUser,
      attributes: {
        ...prevUser.attributes,
        [key]: value,
      },
    }));
  };

  useEffect(() => {
    debugLog(
      `optimizely object ${process.env.version}`,
      optimizely,
      !!optimizely?.isReady(),
      new Date().toISOString(),
    );

    if (!optimizely) {
      return;
    }

    optimizely.onReady().then(() => {
      debugLog("optimizely ready", new Date().toISOString());
      setOptimizelyReady(true);

      optimizely.notificationCenter.addNotificationListener(
        enums.NOTIFICATION_TYPES.DECISION,
        setGTMOptimizelyInfo,
      );
    });
  }, [optimizely]);

  useEffect(() => {
    if (optimizelyUser.attributes.isInTest && testStarted) {
      insertTrackingInfo();
    }
  }, [optimizelyUser, testStarted]);

  useEffect(() => {
    setOptimizelyGTMInfo();
  }, [gtmEventsService]);

  useEffect(() => {
    if (!gtmOpenReplayDataSet && openReplaySessionUrl) {
      const replayUrl = new URL(openReplaySessionUrl);

      gtmEventsService.trackCustom("openreplay-session", {
        openReplaySessionId,
        openReplaySessionUrl: openReplaySessionUrl.replace(
          replayUrl.origin,
          "",
        ),
      });

      setGtmOpenReplayDataSet(true);
    }
  }, [gtmEventsService, openReplaySessionId, openReplaySessionUrl]);

  useEffect(() => {
    if (optimizelyDecisionEvent && gtmEventsService?.isInitialized) {
      const { flagKey, ruleKey, variationKey, decisionEventDispatched } =
        optimizelyDecisionEvent.decisionInfo;

      trackCustom("optimizely-decision-fs", {
        "optimizely-flagKey": flagKey,
        "optimizely-ruleKey": ruleKey,
        "optimizely-variationKey": variationKey,
        decisionEventDispatched: decisionEventDispatched,
        dma: optimizelyDecisionEvent.attributes?.dma,
        dmaGroup: optimizelyDecisionEvent.attributes?.dmaGroup,
        exposedVsHoldoutStatus: optimizelyDecisionEvent.attributes?.dmaGroup,
      });
    }
  }, [gtmEventsService?.isInitialized, optimizelyDecisionEvent]);

  useEffect(() => {
    if (!optimizelyReady) {
      return;
    }

    const qaUserId = isCtvQaUserId(optimizelyUser.id)
      ? optimizelyUser.id
      : null;
    const routeFlags = [
      ...(OPTIMIZELY_ROUTE_FLAGS[pathname] || []),
      ...(OPTIMIZELY_ROUTE_FLAGS["all"] || []),
    ];
    const hasCtvFlags = routeFlags.some((flagKey) => flagKey === "AS202");

    if (optimizelyUser.attributes.isInTest && !qaUserId && !hasCtvFlags) {
      return;
    }

    const runKey = `${pathname}:${qaUserId ?? ""}`;

    if (decidesRunKey.current === runKey) {
      return;
    }

    decidesRunKey.current = runKey;
    runOptimizelyDecides(pathname);
  }, [pathname, optimizelyUser.attributes.isInTest, optimizelyReady]);

  useEffect(() => {
    window.addEventListener("load", insertGAInfo);
    window.addEventListener("storage", onStorageChange);

    return () => {
      window.removeEventListener("load", insertGAInfo);
      window.removeEventListener("storage", onStorageChange);
    };
  }, []);

  useEffect(() => {
    if ((window as any).__oneTrustLoadFailed) {
      setOneTrustGroupsReady(true);
    }
  }, []);

  useEffect(() => {
    debugLog(
      "optimizelyUser updated",
      optimizelyUser,
      new Date().toISOString(),
    );
  }, [optimizelyUser, optimizelyUser.attributes]);

  return (
    <AnalyticsContext.Provider
      value={{
        optimizelyDecisionsRan,
        optimizelyUser,
        setOptimizelyUser,
        getOptimizelyDecisionInfo,
        setOptimizelyUserAttribute,
        oneTrustGroupsReady,
        setOneTrustGroupsReady,
      }}
    >
      {Object.keys(OPTIMIZELY_ROUTE_FLAGS).length > 0 ? (
        <OptimizelyProvider optimizely={optimizely} user={optimizelyUser}>
          {children}
        </OptimizelyProvider>
      ) : (
        children
      )}
    </AnalyticsContext.Provider>
  );
};

export { AnalyticsProvider, AnalyticsContext };

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);

  if (!context) {
    throw new Error("useAnalytics must be used within an AnalyticsProvider");
  }

  return context;
};
